import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

// ===== LIST =====
export const getSenderIds = createAsyncThunk(
  'senderId/getSenderIds',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SENDER_IDS, { params });
      
      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Sender IDs loaded',
          data: data.data || [],      // the actual list
          meta: data.meta || {},      // pagination info
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to load sender IDs',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Get sender IDs error:', error);
      let errorMessage = 'Failed to load sender IDs';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to load sender IDs';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== SINGLE =====
export const getSenderId = createAsyncThunk(
  'senderId/getSenderId',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SENDER_IDS_SHOW(id));
      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Sender ID loaded',
          data: data || null, // data is the sender object
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to load sender ID',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Get sender ID error:', error);
      let errorMessage = 'Failed to load sender ID';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to load sender ID';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== CREATE =====
export const createSenderId = createAsyncThunk(
  'senderId/createSenderId',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SENDER_IDS_CREATE, data);
      const { status, message, data: responseData } = response.data;

      if (status === 200 || status === 201) {
        return {
          success: true,
          message: message || 'Sender ID created',
          data: responseData || null,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to create sender ID',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Create sender ID error:', error);
      let errorMessage = 'Failed to create sender ID';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to create sender ID';
        if (error.response.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== UPDATE =====
export const updateSenderId = createAsyncThunk(
  'senderId/updateSenderId',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(API_ENDPOINTS.SENDER_IDS_UPDATE(id), data);
      const { status, message, data: responseData } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Sender ID updated',
          data: responseData || null,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to update sender ID',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Update sender ID error:', error);
      let errorMessage = 'Failed to update sender ID';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to update sender ID';
        if (error.response.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== DELETE =====
export const deleteSenderId = createAsyncThunk(
  'senderId/deleteSenderId',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.SENDER_IDS_DELETE(id));
      const { status, message } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Sender ID deleted',
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to delete sender ID',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Delete sender ID error:', error);
      let errorMessage = 'Failed to delete sender ID';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to delete sender ID';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== APPROVE =====
export const approveSenderId = createAsyncThunk(
  'senderId/approveSenderId',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SENDER_IDS_APPROVE(id));
      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Sender ID approved',
          data: data || null,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to approve sender ID',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Approve sender ID error:', error);
      let errorMessage = 'Failed to approve sender ID';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to approve sender ID';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== REJECT =====
export const rejectSenderId = createAsyncThunk(
  'senderId/rejectSenderId',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SENDER_IDS_REJECT(id));
      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Sender ID rejected',
          data: data || null,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to reject sender ID',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Reject sender ID error:', error);
      let errorMessage = 'Failed to reject sender ID';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to reject sender ID';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== TOGGLE STATUS =====
export const toggleSenderIdStatus = createAsyncThunk(
  'senderId/toggleSenderIdStatus',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SENDER_IDS_TOGGLE_STATUS(id));
      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Status toggled',
          data: data || null,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to toggle status',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Toggle sender ID status error:', error);
      let errorMessage = 'Failed to toggle status';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to toggle status';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== SET DEFAULT =====
export const setDefaultSenderId = createAsyncThunk(
  'senderId/setDefaultSenderId',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SENDER_IDS_SET_DEFAULT(id));
      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Default sender ID updated',
          data: data || null,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to set default',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Set default sender ID error:', error);
      let errorMessage = 'Failed to set default';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to set default';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== GET DEFAULT =====
export const getDefaultSenderId = createAsyncThunk(
  'senderId/getDefaultSenderId',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SENDER_IDS_DEFAULT);
      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Default sender ID retrieved',
          data: data || null,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to get default sender ID',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Get default sender ID error:', error);
      let errorMessage = 'Failed to get default sender ID';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to get default sender ID';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// =============================================
// SLICE
// =============================================

const initialState = {
  senderIds: [],
  currentSenderId: null,
  defaultSenderId: null,
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  },
  isLoading: false,
  isSubmitting: false,
  error: null,
  successMessage: null,
  apiMessage: null,
};

const senderIdSlice = createSlice({
  name: 'senderId',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearApiMessage: (state) => {
      state.apiMessage = null;
    },
    clearSenderIdState: (state) => {
      state.senderIds = [];
      state.currentSenderId = null;
      state.defaultSenderId = null;
      state.pagination = initialState.pagination;
      state.isLoading = false;
      state.isSubmitting = false;
      state.error = null;
      state.successMessage = null;
      state.apiMessage = null;
    },
    clearCurrentSenderId: (state) => {
      state.currentSenderId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== GET LIST =====
      .addCase(getSenderIds.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(getSenderIds.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload || {};
        state.senderIds = payload.data || [];
        state.pagination = payload.meta || {};
        state.successMessage = payload.message || 'Sender IDs loaded';
        state.apiMessage = payload.message || 'Sender IDs loaded';
        state.error = null;
      })
      .addCase(getSenderIds.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to load sender IDs';
        state.apiMessage = action.payload?.message || 'Failed to load sender IDs';
        state.successMessage = null;
      })

      // ===== GET SINGLE =====
      .addCase(getSenderId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(getSenderId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSenderId = action.payload.data || null;
        state.successMessage = action.payload.message || 'Sender ID loaded';
        state.apiMessage = action.payload.message || 'Sender ID loaded';
        state.error = null;
      })
      .addCase(getSenderId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to load sender ID';
        state.apiMessage = action.payload?.message || 'Failed to load sender ID';
        state.successMessage = null;
      })

      // ===== CREATE =====
      .addCase(createSenderId.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(createSenderId.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Sender ID created';
        state.apiMessage = action.payload.message || 'Sender ID created';
        state.error = null;
      })
      .addCase(createSenderId.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to create sender ID';
        state.apiMessage = action.payload?.message || 'Failed to create sender ID';
        state.successMessage = null;
      })

      // ===== UPDATE =====
      .addCase(updateSenderId.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(updateSenderId.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Sender ID updated';
        state.apiMessage = action.payload.message || 'Sender ID updated';
        state.error = null;
        if (state.currentSenderId?.id === action.payload.data?.id) {
          state.currentSenderId = action.payload.data;
        }
      })
      .addCase(updateSenderId.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to update sender ID';
        state.apiMessage = action.payload?.message || 'Failed to update sender ID';
        state.successMessage = null;
      })

      // ===== DELETE =====
      .addCase(deleteSenderId.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(deleteSenderId.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Sender ID deleted';
        state.apiMessage = action.payload.message || 'Sender ID deleted';
        state.error = null;
      })
      .addCase(deleteSenderId.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to delete sender ID';
        state.apiMessage = action.payload?.message || 'Failed to delete sender ID';
        state.successMessage = null;
      })

      // ===== APPROVE =====
      .addCase(approveSenderId.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(approveSenderId.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Sender ID approved';
        state.apiMessage = action.payload.message || 'Sender ID approved';
        state.error = null;
      })
      .addCase(approveSenderId.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to approve sender ID';
        state.apiMessage = action.payload?.message || 'Failed to approve sender ID';
        state.successMessage = null;
      })

      // ===== REJECT =====
      .addCase(rejectSenderId.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(rejectSenderId.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Sender ID rejected';
        state.apiMessage = action.payload.message || 'Sender ID rejected';
        state.error = null;
      })
      .addCase(rejectSenderId.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to reject sender ID';
        state.apiMessage = action.payload?.message || 'Failed to reject sender ID';
        state.successMessage = null;
      })

      // ===== TOGGLE STATUS =====
      .addCase(toggleSenderIdStatus.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(toggleSenderIdStatus.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Status toggled';
        state.apiMessage = action.payload.message || 'Status toggled';
        state.error = null;
      })
      .addCase(toggleSenderIdStatus.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to toggle status';
        state.apiMessage = action.payload?.message || 'Failed to toggle status';
        state.successMessage = null;
      })

      // ===== SET DEFAULT =====
      .addCase(setDefaultSenderId.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(setDefaultSenderId.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Default sender ID updated';
        state.apiMessage = action.payload.message || 'Default sender ID updated';
        state.error = null;
        // Update the list to reflect new default
        if (state.senderIds.length > 0 && action.payload.data) {
          state.senderIds = state.senderIds.map(item => ({
            ...item,
            is_default: item.id === action.payload.data.id,
          }));
        }
      })
      .addCase(setDefaultSenderId.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to set default';
        state.apiMessage = action.payload?.message || 'Failed to set default';
        state.successMessage = null;
      })

      // ===== GET DEFAULT =====
      .addCase(getDefaultSenderId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(getDefaultSenderId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.defaultSenderId = action.payload.data || null;
        state.successMessage = action.payload.message || 'Default sender ID retrieved';
        state.apiMessage = action.payload.message || 'Default sender ID retrieved';
        state.error = null;
      })
      .addCase(getDefaultSenderId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to get default sender ID';
        state.apiMessage = action.payload?.message || 'Failed to get default sender ID';
        state.successMessage = null;
        state.defaultSenderId = null;
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  clearApiMessage,
  clearSenderIdState,
  clearCurrentSenderId,
} = senderIdSlice.actions;

// ===== SELECTORS =====
export const selectSenderIds = (state) => state.senderId.senderIds;
export const selectSenderIdPagination = (state) => state.senderId.pagination;
export const selectCurrentSenderId = (state) => state.senderId.currentSenderId;
export const selectDefaultSenderId = (state) => state.senderId.defaultSenderId;
export const selectSenderIdLoading = (state) => state.senderId.isLoading;
export const selectSenderIdSubmitting = (state) => state.senderId.isSubmitting;
export const selectSenderIdApiMessage = (state) => state.senderId.apiMessage;
export const selectSenderIdError = (state) => state.senderId.error;
export const selectSenderIdSuccess = (state) => state.senderId.successMessage;

export default senderIdSlice.reducer;